import { useEffect, useState } from 'react';

import {
  CALLOUT_W,
  HANDLE_W,
  LEFT_FRACTION,
  RIGHT_OPEN_FROM,
  calloutFitsBeside,
  listMinWidth,
  rightSidebarHolds,
  rightSidebarWidth,
  viewToggleX,
} from '../../components/Frame';
import { MAX_FRACTION, MIN_FRACTION } from '../../components/ResizableSidebar';
import type { ToggleSpan } from '../../components/viewToggleMetrics';

interface Options {
  /** Below 900px, where there are no sidebars. */
  isNarrow: boolean;
  /** A callout is open, and will be the right sidebar wherever it fits. */
  callout: boolean;
  /** Closes it: the right sidebar's toggle, or filters asked for where there is no room. */
  onCloseCallout: () => void;
  /** How far the pair of view toggles reaches either side of where it is anchored. */
  span: ToggleSpan;
}

/**
 * Explore's two sidebars from 900px up, and what fits between them: the filters
 * on the left, and on the right its own panel or the callout. Also where the
 * view toggles sit, which every scope and layout shares so that switching
 * never moves them.
 *
 * The page's width, and the list's scrollbar where it takes room, decide how
 * wide the filters may be before a ticket drops below its minimum.
 */
export function useSidebars({ isNarrow, callout, onCloseCallout, span }: Options) {
  /** Each has its toggle at a top corner of the list. */
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(
    () => window.matchMedia(`(min-width: ${RIGHT_OPEN_FROM}px)`).matches,
  );
  /** The filters' width as dragged, as a share of the page. Null: the default. */
  const [leftShare, setLeftShare] = useState<number | null>(null);

  const [pageW, setPageW] = useState(() => window.innerWidth);
  useEffect(() => {
    const onResize = () => setPageW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // The list keeps a scrollbar's width in reserve (`scrollbar-gutter: stable`):
  // none where scrollbars overlay the content, about 15px where they do not.
  const [scrollbar] = useState(() => {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll';
    document.body.appendChild(probe);
    const width = probe.offsetWidth - probe.clientWidth;
    probe.remove();
    return width;
  });

  /** The widest the filters may be beside a right sidebar this wide, in pixels. */
  const leftRoom = (rightW: number) =>
    Math.min(pageW * MAX_FRACTION, pageW - HANDLE_W - rightW - listMinWidth(pageW, scrollbar));
  /**
   * The filters beside a right sidebar this wide. Where not even the narrowest
   * fit beside it and a readable list, they step aside, and come back when it
   * closes. Beside the callout, filters dragged too wide for it are drawn at
   * their default, and get their width back when it closes.
   */
  const filtersBeside = (rightW: number, beside: boolean) => {
    const room = leftRoom(rightW);
    const byDefault = pageW >= RIGHT_OPEN_FROM ? LEFT_FRACTION.wide : LEFT_FRACTION.narrow;
    const share = leftShare !== null && !(beside && leftShare * pageW > room) ? leftShare : byDefault;
    const shown = leftOpen && room >= pageW * MIN_FRACTION;
    const fraction = Math.min(share, room / pageW);
    // Where they end, handle included.
    return { shown, fraction, edge: shown ? fraction * pageW + HANDLE_W : 0 };
  };
  /**
   * Where the toggle sits. Measured off the filters at rest beside the panel, not
   * beside whatever is open: a callout stepping them aside must not move it.
   */
  const toggleX = viewToggleX(pageW, filtersBeside(rightSidebarWidth(pageW), false).edge, span);
  /** The callout can be the right sidebar at this width; narrower, it is drawn over the list. */
  const calloutFits =
    !isNarrow && calloutFitsBeside(pageW, scrollbar, filtersBeside(CALLOUT_W, true).edge, toggleX, span);
  /** What Explore's right sidebar holds. My Schedule asks the same of its own panel. */
  const rightHolds = rightSidebarHolds(calloutFits && callout, rightOpen);
  const rightW = rightHolds === 'callout' ? CALLOUT_W : rightHolds ? rightSidebarWidth(pageW) : 0;
  const filters = filtersBeside(rightW, rightHolds === 'callout');

  const toggleRight = () => {
    if (rightHolds) {
      // The callout goes with the sidebar it is in.
      if (rightHolds === 'callout') onCloseCallout();
      setRightOpen(false);
      return;
    }
    // Filters dragged out far enough to squeeze the list go back to their
    // default width, rather than leaving the list at its narrowest.
    if (leftShare !== null && leftShare * pageW > leftRoom(rightSidebarWidth(pageW))) {
      setLeftShare(null);
    }
    setRightOpen(true);
  };

  const toggleLeft = () => {
    if (filters.shown) {
      setLeftOpen(false);
      return;
    }
    setLeftOpen(true);
    // Asked for where there is no room for them: the right sidebar makes way,
    // the callout in it first, and then the panel under it if that is too wide.
    if (leftRoom(rightW) >= pageW * MIN_FRACTION) return;
    if (rightHolds === 'callout') onCloseCallout();
    if (rightOpen && leftRoom(rightSidebarWidth(pageW)) < pageW * MIN_FRACTION) setRightOpen(false);
  };

  return {
    calloutFits,
    rightHolds,
    /** Explore's own panel is open, whether or not a callout is over it just now. */
    rightOpen,
    filtersShown: filters.shown,
    leftFraction: filters.fraction,
    /** The filters' drag stops where the list would drop below its minimum. */
    maxLeftFraction: leftRoom(rightW) / pageW,
    /** Where the filters end, handle included: zero when they are hidden or aside. */
    filtersEdge: filters.edge,
    toggleX,
    setLeftShare,
    toggleLeft,
    toggleRight,
  };
}
